import { useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

export function PublicLayout() {
  const { t, locale } = useTranslation();
  const [currentLocale, setCurrentLocale] = useState(locale);

  const toggleLocale = () => {
    const next = currentLocale === 'it' ? 'en' : 'it';
    setCurrentLocale(next);
    localStorage.setItem('flymind_locale', next);
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 border-b border-border bg-surface/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">FM</span>
            </div>
            <span className="text-lg font-bold text-text-primary">FlyMind</span>
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleLocale}
              className="rounded-lg px-2 py-1 text-xs font-medium text-text-secondary hover:text-text-primary border border-border transition-colors cursor-pointer"
            >
              {currentLocale === 'it' ? 'EN' : 'IT'}
            </button>
            <Link
              to="/login"
              className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              {t('auth.login')}
            </Link>
            <Link
              to="/register"
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
            >
              {t('auth.register')}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-surface-secondary">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-lg bg-primary-600 flex items-center justify-center">
                  <span className="text-white font-bold text-xs">FM</span>
                </div>
                <span className="font-bold text-text-primary">FlyMind</span>
              </div>
              <p className="text-sm text-text-secondary">
                {t('landing.footer.description')}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <a
                  href="https://www.instagram.com/flymind.travel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-[#E4405F] transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
                <a
                  href="https://t.me/flymindtravel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-[#0088cc] transition-colors"
                  aria-label="Telegram"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/joinflymind"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-text-secondary hover:text-[#1877F2] transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 className="mb-3 text-sm font-semibold text-text-primary">
                {t('landing.footer.product')}
              </h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><Link to="/register" className="hover:text-text-primary transition-colors">{t('landing.hero.cta')}</Link></li>
                <li><Link to="/#pricing" className="hover:text-text-primary transition-colors">{t('landing.pricing.title')}</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="mb-3 text-sm font-semibold text-text-primary">
                {t('landing.footer.company')}
              </h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><Link to="/about" className="hover:text-text-primary transition-colors">{t('landing.footer.about')}</Link></li>
                <li><Link to="/blog" className="hover:text-text-primary transition-colors">{t('landing.footer.blog')}</Link></li>
                <li><Link to="/contact" className="hover:text-text-primary transition-colors">{t('landing.footer.contact')}</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="mb-3 text-sm font-semibold text-text-primary">
                {t('landing.footer.legal')}
              </h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li><Link to="/terms" className="hover:text-text-primary transition-colors">{t('landing.footer.terms')}</Link></li>
                <li><Link to="/privacy" className="hover:text-text-primary transition-colors">{t('landing.footer.privacy')}</Link></li>
                <li><Link to="/cookies" className="hover:text-text-primary transition-colors">{t('landing.footer.cookies')}</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 border-t border-border pt-6 text-center text-xs text-text-secondary">
            &copy; {new Date().getFullYear()} FlyMind. {t('landing.footer.allRightsReserved')}
          </div>
        </div>
      </footer>
    </div>
  );
}
